import { Link } from "react-router-dom";
import StudyGuideShell from "../courses/StudyGuideShell";
import "../multivariableCalculus/PartialDerivativesGuide.css";
import { LaMcqSection } from "./LaMcq";
import {
  LA_E_INTRO_QUIZ,
  LA_E_CHAR_QUIZ,
  LA_E_DIAG_QUIZ,
  LA_E_APPS_QUIZ,
} from "../../data/laSystemsEigenQuizzes";
import { TheoryBox, TheoremBox, ProcedureBox, WorkedExample, PracticalTheory, RealLifeUse } from "./LaBlocks";

import LaCertificateBoost from "./LaCertificateBoost";

function Divider() {
  return <hr className="divider" />;
}

function EigenGuide({ part = 1 }) {
  if (part === 2) {
    return (
      <StudyGuideShell guideClass="partial-derivatives-guide" title="Eigenvalues & Eigenvectors (Part 2)">
        <nav className="sidebar">
          <div className="sb-brand"><div className="sb-title">Eigen · Part 2</div></div>
          <a className="sb-link" href="#la-e-diag">Diagonalization</a>
          <a className="sb-link" href="#la-e-proc2">Method</a>
          <a className="sb-link" href="#la-e-spectral">Spectral theorem</a>
          <a className="sb-link" href="#la-e-ex-p2">Examples</a>
          <a className="sb-link" href="#quiz-la-e-diag">Quiz</a>
          <a className="sb-link" href="#la-e-apps">Applications</a>
          <a className="sb-link" href="#quiz-la-e-apps">Quiz</a>
          <a className="sb-link" href="#la-cert-eigen-p2">Eight examples</a>
        </nav>
        <main className="main">
          <header className="ch-hdr">
            <div className="ch-eye">Linear Algebra · Part 2 of 2</div>
            <h1 className="ch-title">Eigenvalues &amp; Eigenvectors</h1>
            <p className="ch-sub">Diagonalization, matrix powers, the Spectral Theorem, and applications — theory plus calculations</p>
            <span className="ch-orn">✦ &nbsp; ✦ &nbsp; ✦</span>
          </header>

          <section className="section" id="la-e-diag">
            <div className="sec-badge">Section 4.3</div>
            <h2 className="sec-title">Diagonalization — deep theory</h2>
            <p>
              {"Diagonalization is the payoff of the eigenstory: in an eigenbasis the matrix becomes a list of independent stretch factors on the diagonal. Powers, exponentials, and many dynamical systems collapse to scalar arithmetic once $A=PDP^{-1}$."}
            </p>
            <TheoryBox title="A = PDP⁻¹">
              <p>
                {"$A$ is diagonalizable if there exist invertible $P$ and diagonal $D$ with $A=PDP^{-1}$. Equivalently, $\\mathbb{R}^n$ has a basis of eigenvectors of $A$. Columns of $P$ are those independent eigenvectors; diagonal entries of $D$ are the matching eigenvalues in the same order. Then $AP=PD$, which is exactly $A$ times each column of $P$ equals $\\lambda$ times that column."}
              </p>
              <p>
                {"Once diagonalized, $A^k=P D^k P^{-1}$ with $D^k=\\mathrm{diag}(\\lambda_1^k,\\ldots,\\lambda_n^k)$. The matrix exponential $e^{At}=P e^{Dt} P^{-1}$ is the continuous-time analogue used for $\\dot x=Ax$."}
              </p>
            </TheoryBox>
            <PracticalTheory title="Eigen workflow that stays organized">
              <p>
                {"Compute $p(\\lambda)=\\det(A-\\lambda I)$, factor for eigenvalues, then for each $\\lambda$ row-reduce $(A-\\lambda I)$ to read the eigenspace. If you need powers or a decoupled basis, check whether you have $n$ independent eigenvectors (diagonalizable). For symmetric $A$, expect an orthonormal eigenbasis."}
              </p>
            </PracticalTheory>
            <TheoremBox title="Enough eigenvectors">
              <p>
                {"$n$ distinct eigenvalues in $\\mathbb{R}^n$ automatically give $n$ independent eigenvectors, hence diagonalizability. For a repeated eigenvalue you need geometric multiplicity equal to algebraic multiplicity to fill an eigenbasis. If geo. mult. is strictly smaller, $A$ is defective and not diagonalizable over that field (Jordan form is the next tool)."}
              </p>
            </TheoremBox>
            <RealLifeUse>{"PageRank is a dominant-eigenvector problem; vibration modes of buildings and molecules are eigenpairs; PCA finds principal directions as eigenvectors of a covariance matrix; population models use eigenvalues of a Leslie/transition matrix to predict long-run growth."}</RealLifeUse>
            <TheoryBox title="Symmetric bonus">
              <p>
                {"Real symmetric matrices are always orthogonally diagonalizable: $A=Q\\Lambda Q^T$ with $Q^{-1}=Q^T$. Eigenvalues are real, and eigenvectors for distinct eigenvalues are automatically orthogonal. This is the spectral theorem that underlies PCA and quadratic forms."}
              </p>
            </TheoryBox>
            <PracticalTheory title="Hand-calculation habits">
              <p>
                {"Name the objects (vectors, matrix size, unknowns) before computing. Prefer a method you can check: a second expansion, a substitution back into $Ax=b$, or a quick rank/$\\det$ sanity test."}
              </p>
            </PracticalTheory>
            <RealLifeUse>{"The same checklist shows up in engineering solvers, spreadsheet models, and any pipeline that turns measurements into a linear map — clear setup prevents silent size and dependence bugs."}</RealLifeUse>
          </section>

          <section className="section" id="la-e-proc2">
            <div className="sec-badge">Procedure</div>
            <h2 className="sec-title">How to diagonalize a matrix</h2>
            <ProcedureBox
              title="How to diagonalize A (when possible)"
              steps={[
                { text: "Compute the characteristic polynomial $p(\\lambda)=\\det(A-\\lambda I)$ and find all eigenvalues.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "For each eigenvalue $\\lambda$, solve $(A-\\lambda I)v=0$ and find a basis of the eigenspace.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "Check dimensions: if you obtain fewer than $n$ independent eigenvectors in total, stop — $A$ is not diagonalizable.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Form $P$ with those eigenvectors as columns, and $D=\\mathrm{diag}(\\lambda_1,\\ldots,\\lambda_n)$ in matching order.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Optional but recommended: compute $P^{-1}$ and verify $A=PDP^{-1}$ on a couple of entries, or check $AP=PD$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "To compute powers: replace $A^k$ by $P D^k P^{-1}$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "For real symmetric $A$, orthonormalize eigenvectors within each eigenspace so that $P$ can be taken orthogonal ($P^{-1}=P^T$).", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
            />
          </section>


          <section className="section" id="la-e-spectral">
            <div className="sec-badge">Spectral Theorem</div>
            <h2 className="sec-title">Real symmetric matrices — orthogonal diagonalization</h2>
            <TheoremBox title="Spectral Theorem for real symmetric matrices">
              <p>
                {"If $A\\in\\mathbb{R}^{n\\times n}$ is symmetric ($A=A^T$), then every eigenvalue of $A$ is real and $\\mathbb{R}^n$ has an orthonormal basis of eigenvectors of $A$. Equivalently, there is an orthogonal matrix $Q$ and a real diagonal matrix $\\Lambda$ such that $A=Q\\Lambda Q^T$. Conversely, any real matrix of the form $Q\\Lambda Q^T$ with $Q$ orthogonal and $\\Lambda$ real diagonal is symmetric."}
              </p>
              <p>
                {"Eigenvectors belonging to distinct eigenvalues are automatically orthogonal. If an eigenvalue is repeated, choose an orthonormal basis inside its eigenspace; combining those bases gives the orthogonal matrix $Q$. Repeated eigenvalues therefore do not prevent diagonalization of a real symmetric matrix."}
              </p>
            </TheoremBox>
            <TheoryBox title="Spectral decomposition and matrix functions">
              <p>
                {"Write the orthonormal eigenvectors as $q_1,\\ldots,q_n$ with matching eigenvalues $\\lambda_1,\\ldots,\\lambda_n$. Then $A=\\sum_{i=1}^{n}\\lambda_i q_iq_i^T$. Each matrix $q_iq_i^T$ is the orthogonal projector onto the eigenvector direction $q_i$."}
              </p>
              <p>
                {"Because $Q^{-1}=Q^T$, powers become $A^k=Q\\Lambda^kQ^T=\\sum_i\\lambda_i^k q_iq_i^T$. More generally, whenever a scalar function $f$ is defined on the eigenvalues, $f(A)=Qf(\\Lambda)Q^T$. This is why symmetric eigenproblems are especially powerful in engineering and data analysis."}
              </p>
            </TheoryBox>
            <ProcedureBox
              title="How to orthogonally diagonalize a real symmetric matrix"
              steps={[
                { text: "Verify $A=A^T$.", why: "The real Spectral Theorem applies to symmetric matrices." },
                { text: "Find the eigenvalues from the characteristic equation.", why: "A real symmetric matrix has only real eigenvalues." },
                { text: "Find a basis for each eigenspace $\\operatorname{Nul}(A-\\lambda I)$.", why: "Each basis vector is an eigenvector for that eigenvalue." },
                { text: "Normalize the eigenvectors; if an eigenspace has dimension greater than $1$, orthonormalize within that eigenspace if necessary.", why: "Columns of $Q$ must be orthonormal." },
                { text: "Place the orthonormal eigenvectors in $Q$ and the matching eigenvalues in $\\Lambda$.", why: "Column order in $Q$ must match diagonal order in $\\Lambda$." },
                { text: "Verify $Q^TQ=I$ and $AQ=Q\\Lambda$; then $A=Q\\Lambda Q^T$.", why: "These checks verify both orthogonality and the eigenpair matching." }
              ]}
            />
            <RealLifeUse>{"For vibration and modal analysis, symmetric mass-normalized models separate motion into orthogonal modes. For PCA, a covariance matrix is symmetric, so its orthonormal eigenvectors give mutually perpendicular principal directions and its eigenvalues measure variance along those directions."}</RealLifeUse>
          </section>

          <section className="section" id="la-e-ex-p2">
            <div className="sec-badge">Large examples</div>
            <h2 className="sec-title">Eight detailed worked examples</h2>

            <WorkedExample
              number={1}
              title="Diagonalize a 2×2 triangular matrix"
              setup={"$A=\\begin{pmatrix}2&1\\\\0&3\\end{pmatrix}$."}
              steps={[
                { text: "Triangular ⇒ eigenvalues are diagonal entries $\\lambda=2$ and $\\lambda=3$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "For $\\lambda=2$: $A-2I=\\begin{pmatrix}0&1\\\\0&1\\end{pmatrix}$. Equation $v_2=0$. Take $v_1=(1,0)$." },
                { text: "For $\\lambda=3$: $A-3I=\\begin{pmatrix}-1&1\\\\0&0\\end{pmatrix}$. Equation $-v_1+v_2=0$. Take $v_2=(1,1)$." },
                { text: "Two independent eigenvectors ⇒ diagonalizable.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Set $P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$, $D=\\mathrm{diag}(2,3)$." },
                { text: "Check $AP=PD$: both sides equal $\\begin{pmatrix}2&3\\\\0&3\\end{pmatrix}$.", why: "Confirm with a second method or by substituting back." }
              ]}
              result={"$A=PDP^{-1}$ with $P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$, $D=\\mathrm{diag}(2,3)$."}
              check={"$\\det P=1\\neq 0$, so $P$ is invertible."}
            />
            <WorkedExample
              number={2}
              title="Compute A⁵ via diagonalization"
              setup={"Same $A$ as Example 1. Find $A^5$ using $P,D$."}
              steps={[
                { text: "$A^5=P D^5 P^{-1}$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "$D^5=\\mathrm{diag}(2^5,3^5)=\\mathrm{diag}(32,243)$." },
                { text: "From Example 1, $P=\\begin{pmatrix}1&1\\\\0&1\\end{pmatrix}$, so $P^{-1}=\\begin{pmatrix}1&-1\\\\0&1\\end{pmatrix}$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "First $D^5 P^{-1}=\\begin{pmatrix}32&-32\\\\0&243\\end{pmatrix}$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "Then $A^5=P(D^5 P^{-1})=\\begin{pmatrix}32&211\\\\0&243\\end{pmatrix}$.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "Direct multiplication of $A$ five times would be painful; diagonalization scales.", why: "Diagonalize when possible so powers become trivial on D." }
              ]}
              result={"$A^5=\\begin{pmatrix}32&211\\\\0&243\\end{pmatrix}$."}
              check={"Trace of $A^5$ should be $32+243=275=2^5+3^5$."}
            />
            <WorkedExample
              number={3}
              title="Not every matrix diagonalizes over R"
              setup={"Rotation by $90^\\circ$: $R=\\begin{pmatrix}0&-1\\\\1&0\\end{pmatrix}$."}
              steps={[
                { text: "$R-\\lambda I=\\begin{pmatrix}-\\lambda&-1\\\\1&-\\lambda\\end{pmatrix}$." },
                { text: "$\\det=\\lambda^2+1=0\\Rightarrow\\lambda=\\pm i$.", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "No real eigenvalues ⇒ no real eigenvectors ⇒ not diagonalizable over $\\mathbb{R}$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Over $\\mathbb{C}$ it does diagonalize, with complex conjugate eigenpairs.", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "Geometrically every nonzero vector is rotated, never merely scaled, so no real eigenline.", why: "Read the geometric meaning of the algebraic result." },
                { text: "Invertibility is irrelevant here: $\\det R=1\\neq 0$, yet real diagonalization fails.", why: "Diagonalize when possible so powers become trivial on D." }
              ]}
              result={"$R$ is not diagonalizable over $\\mathbb{R}$."}
              check={"$\\lambda^2+1$ has no real root."}
            />
            <WorkedExample
              number={4}
              title="Symmetric ⇒ orthogonal diagonalization"
              setup={"$S=\\begin{pmatrix}2&1\\\\1&2\\end{pmatrix}$."}
              steps={[
                { text: "$\\det(S-\\lambda I)=(2-\\lambda)^2-1=\\lambda^2-4\\lambda+3=(\\lambda-1)(\\lambda-3)$.", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "Eigenvalues $\\lambda=1$ and $\\lambda=3$ (real, as promised for symmetric matrices).", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "For $\\lambda=1$: $(S-I)=\\begin{pmatrix}1&1\\\\1&1\\end{pmatrix}\\Rightarrow v_1+v_2=0$. Unit vector $u_1=\\frac{1}{\\sqrt{2}}(1,-1)$.", why: "Use dots and norms for length, angle, and orthogonal projection." },
                { text: "For $\\lambda=3$: $(S-3I)=\\begin{pmatrix}-1&1\\\\1&-1\\end{pmatrix}\\Rightarrow -v_1+v_2=0$. Unit $u_2=\\frac{1}{\\sqrt{2}}(1,1)$." },
                { text: "These are orthonormal; $Q=[u_1\\ u_2]$ is orthogonal and $S=Q\\Lambda Q^T$ with $\\Lambda=\\mathrm{diag}(1,3)$.", why: "Use dots and norms for length, angle, and orthogonal projection." },
                { text: "Dot product $u_1\\cdot u_2=0$ illustrates orthogonality of distinct eigenspaces.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
              result={"$S$ is orthogonally diagonalizable with $\\lambda=1,3$."}
              check={"$u_1\\cdot u_2=0$ and $\\|u_i\\|=1$."}
            />
            <WorkedExample
              number={5}
              title="Defective matrix (not diagonalizable)"
              setup={"$N=\\begin{pmatrix}2&1\\\\0&2\\end{pmatrix}$. Show it fails to diagonalize."}
              steps={[
                { text: "Characteristic polynomial $(\\lambda-2)^2$; algebraic multiplicity of $2$ is $2$.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "$N-2I=\\begin{pmatrix}0&1\\\\0&0\\end{pmatrix}$; solutions satisfy $v_2=0$." },
                { text: "Eigenspace is $\\mathrm{Span}\\{(1,0)\\}$ — geometric multiplicity $1<2$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Only one independent eigenvector ⇒ cannot build invertible $P$ of eigenvectors.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Hence $N$ is not diagonalizable (Jordan block structure).", why: "Diagonalize when possible so powers become trivial on D." },
                { text: "Contrast with Example 1, where the off-diagonal $1$ sat with unequal diagonal entries and two eigenlines appeared." }
              ]}
              result={"$N$ is defective: not diagonalizable."}
              check={"Any supposed eigenbasis would need two independent vectors for $\\lambda=2$, but $\\dim\\mathrm{Nul}(N-2I)=1$."}
            />
            <WorkedExample
              number={6}
              title="Change of basis interpretation"
              setup={"Using $P,D$ from Example 1, interpret $A=PDP^{-1}$ on a vector $x$."}
              steps={[
                { text: "Write $x=P c$, so $c=P^{-1}x$ are coordinates of $x$ in the eigenbasis (columns of $P$).", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Then $Ax=P D c$: in eigen-coordinates, $A$ simply multiplies each coordinate $c_i$ by $\\lambda_i$.", why: "Interpret Ax as a combination of the columns of A." },
                { text: "Finally $P$ maps those scaled coordinates back to the standard basis.", why: "Use independence and spanning (via rank/pivots) to decide bases and membership." },
                { text: "For $x=(1,0)^T$ (already an eigenvector): $c=(1,0)$, $Dc=(2,0)$, $PDc=(2,0)=Ax$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "For $x=(1,1)^T$: $c=(0,1)$, $Dc=(0,3)$, $PDc=(3,3)=3x$." },
                { text: "Diagonalization is exactly “change to eigenbasis, stretch, change back.”", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
              result={"$A$ acts as independent scalings in the eigenbasis of $P$."}
              check={"$A(1,1)^T=(3,3)^T=3(1,1)^T$."}
            />

            <WorkedExample
              number={7}
              title="Orthogonally diagonalize a 3×3 symmetric matrix"
              setup={"$A=\\begin{pmatrix}2&1&0\\\\1&2&0\\\\0&0&4\\end{pmatrix}$. Find $Q$ and $\\Lambda$ with $A=Q\\Lambda Q^T$."}
              steps={[
                { text: "$A=A^T$, so the Spectral Theorem guarantees an orthonormal eigenbasis." },
                { text: "$\\det(A-\\lambda I)=(4-\\lambda)[(2-\\lambda)^2-1]$, so the eigenvalues are $1,3,4$.", why: "Factor the $2\\times2$ symmetric block and the isolated third coordinate." },
                { text: "For $\\lambda=1$, take $v_1=(1,-1,0)$; for $\\lambda=3$, take $v_2=(1,1,0)$; for $\\lambda=4$, take $v_3=(0,0,1)$." },
                { text: "The three eigenvectors are mutually orthogonal: $v_1\\cdot v_2=0$, and both are orthogonal to $v_3$." },
                { text: "Normalize: $q_1=\\frac{1}{\\sqrt2}(1,-1,0)$, $q_2=\\frac{1}{\\sqrt2}(1,1,0)$, $q_3=(0,0,1)$." },
                { text: "Set $Q=[q_1\\ q_2\\ q_3]$ and $\\Lambda=\\operatorname{diag}(1,3,4)$. Then $Q^TQ=I$ and $AQ=Q\\Lambda$." }
              ]}
              result={"$A=Q\\operatorname{diag}(1,3,4)Q^T$ with orthonormal eigenvector columns $q_1,q_2,q_3$."}
              check={"Trace check: $1+3+4=8=\\operatorname{tr}(A)$; determinant check: $1\\cdot3\\cdot4=12=\\det(A)$."}
            />
            <WorkedExample
              number={8}
              title="Closed formula for Aᵏ using the Spectral Theorem"
              setup={"For $A=\\begin{pmatrix}2&1\\\\1&2\\end{pmatrix}$, derive a formula for $A^k$ for integers $k\\ge 0$."}
              steps={[
                { text: "From the symmetric eigenproblem, $q_1=\\frac{1}{\\sqrt2}(1,-1)$ has eigenvalue $1$ and $q_2=\\frac{1}{\\sqrt2}(1,1)$ has eigenvalue $3$." },
                { text: "Thus $A=Q\\operatorname{diag}(1,3)Q^T$ and $A^k=Q\\operatorname{diag}(1,3^k)Q^T$.", why: "Powers act only on the diagonal eigenvalues." },
                { text: "Using spectral projectors, $A^k=1^k q_1q_1^T+3^k q_2q_2^T$." },
                { text: "$q_1q_1^T=\\frac12\\begin{pmatrix}1&-1\\\\-1&1\\end{pmatrix}$ and $q_2q_2^T=\\frac12\\begin{pmatrix}1&1\\\\1&1\\end{pmatrix}$." },
                { text: "Add the two terms to get $A^k=\\frac12\\begin{pmatrix}3^k+1&3^k-1\\\\3^k-1&3^k+1\\end{pmatrix}$." },
                { text: "For $k=0$ the formula gives $I$; for $k=1$ it gives $A$, providing two quick checks." }
              ]}
              result={"$A^k=\\frac12\\begin{pmatrix}3^k+1&3^k-1\\\\3^k-1&3^k+1\\end{pmatrix}$ for $k\\ge0$."}
              check={"At $k=2$, the formula gives $\\begin{pmatrix}5&4\\\\4&5\\end{pmatrix}$, which equals $A^2$."}
            />
          </section>

          <LaMcqSection
            id="quiz-la-e-diag"
            badge="Quiz 4.3"
            title="Diagonalization & Spectral Theorem"
            scoreId="score-la-e-diag"
            section="la-e-diag"
            questions={LA_E_DIAG_QUIZ}
          />

          <Divider />

          <section className="section" id="la-e-apps">
            <div className="sec-badge">Section 4.4</div>
            <h2 className="sec-title">Why eigenvalues matter — deep theory</h2>
            <TheoryBox title="Applications map">
              <p>
                {"Stability of $\\dot x=Ax$ is governed by real parts of eigenvalues: positive ⇒ growth, negative ⇒ decay, zero ⇒ borderline cases needing more care. Discrete powers $A^k x_0$ grow when some $|\\lambda|>1$. PCA finds principal directions as eigenvectors of a covariance / Gram matrix. Vibration modes, PageRank’s dominant eigenvector, and the definiteness of quadratic forms $x^T Ax$ are all eigenproblems."}
              </p>
              <p>
                {"Real symmetric matrices are especially friendly: real eigenvalues and an orthonormal eigenbasis. Indefinite, positive definite, or negative definite quadratic forms are classified by the signs of those eigenvalues."}
              </p>
            </TheoryBox>
            <TheoremBox title="Quick stability and PCA facts">
              <p>
                {"For $\\dot x=Ax$ with diagonalizable $A$, each eigen-component evolves like $e^{\\lambda t}$. For PCA, the leading eigenvector of the covariance matrix points along maximal variance; projecting onto the top $k$ eigenvectors gives the best rank-$k$ approximation in a precise least-squares sense (Eckart–Young for SVD, closely related)."}
              </p>
            </TheoremBox>
          </section>

          <LaMcqSection
            id="quiz-la-e-apps"
            badge="Quiz 4.4"
            title="Applications"
            scoreId="score-la-e-apps"
            section="la-e-apps"
            questions={LA_E_APPS_QUIZ}
          />

          <Divider />
          <LaCertificateBoost topic="eigen" part={2} />

          <section className="section" id="summary">
            <div className="sec-badge">Reference</div>
            <h2 className="sec-title">Module complete</h2>
            <p>
              {"Diagonalization turns matrix powers and linear ODEs into scalar problems; applications from stability to PCA all rest on eigenpairs."}
            </p>
            <p>
              Drill in the{" "}
              <Link to="/practice" style={{ color: "var(--gold)", fontWeight: 600 }}>
                Practice Arena
              </Link>{" "}
              or return via the gold bar to the course hub.
            </p>
          </section>
        </main>
      </StudyGuideShell>
    );
  }

  return (
    <StudyGuideShell guideClass="partial-derivatives-guide" title="Eigenvalues & Eigenvectors (Part 1)">
      <nav className="sidebar">
        <div className="sb-brand"><div className="sb-title">Eigen · Part 1</div></div>
        <a className="sb-link" href="#la-e-intro">Theory</a>
        <a className="sb-link" href="#la-e-proc1">Method</a>
        <a className="sb-link" href="#la-e-ex-p1">Examples</a>
        <a className="sb-link" href="#quiz-la-e-intro">Quiz</a>
        <a className="sb-link" href="#la-e-char">Characteristic poly</a>
        <a className="sb-link" href="#la-e-multiplicity">Multiplicity</a>
        <a className="sb-link" href="#quiz-la-e-char">Quiz</a>
        <a className="sb-link" href="#la-cert-eigen-p1">Eight examples</a>
      </nav>
      <main className="main">
        <header className="ch-hdr">
          <div className="ch-eye">Linear Algebra · Part 1 of 2</div>
          <h1 className="ch-title">Eigenvalues &amp; Eigenvectors</h1>
          <p className="ch-sub">Special directions of a linear map — theory and calculations</p>
          <span className="ch-orn">✦ &nbsp; ✦ &nbsp; ✦</span>
        </header>

        <section className="section" id="la-e-intro">
          <div className="sec-badge">Section 4.1</div>
          <h2 className="sec-title">Definition — deep theory</h2>
          <p>
            {"Most vectors are moved in complicated ways by a linear map. Eigenvectors are the exception: they keep their direction (or reverse it) and are only stretched. Finding them reduces a matrix to its simplest directional behavior."}
          </p>
          <TheoryBox title="Av = λv">
            <p>
              {"A nonzero vector $v$ is an eigenvector of $A$ with eigenvalue $\\lambda$ when $Av=\\lambda v$. Rearrangement gives $(A-\\lambda I)v=0$ with $v\\neq 0$, so $A-\\lambda I$ is singular and $\\det(A-\\lambda I)=0$. The set of all eigenvectors for a fixed $\\lambda$, together with $0$, is the eigenspace $\\mathrm{Nul}(A-\\lambda I)$ — always a subspace."}
            </p>
            <p>
              {"Eigenvalues may be real or complex; over $\\mathbb{R}$ some matrices have no eigenpairs at all (e.g. a $90^\\circ$ rotation). Scaling $v$ by a nonzero constant preserves the eigen-relation: eigenvectors are directions, not unique vectors."}
            </p>
          </TheoryBox>
          <TheoremBox title="Invariant lines">
            <p>
              {"If $Av=\\lambda v$, the line $\\mathrm{Span}\\{v\\}$ is invariant under $A$: every vector on that line is mapped to another on the same line. For $\\lambda>0$ the map stretches; for $\\lambda<0$ it also reverses orientation on that line; for $\\lambda=0$ the whole line collapses to the origin (nullspace direction)."}
            </p>
          </TheoremBox>
          <TheoryBox title="Trace and determinant previews">
            <p>
              {"For any square matrix, the sum of eigenvalues (with algebraic multiplicity) equals $\\mathrm{tr}(A)$, and the product equals $\\det A$. These identities are excellent sanity checks after you solve the characteristic equation."}
            </p>
          </TheoryBox>
        </section>

        <section className="section" id="la-e-proc1">
          <div className="sec-badge">Procedure</div>
          <h2 className="sec-title">How to find eigenvalues and eigenvectors</h2>
          <ProcedureBox
            title="How to compute eigenpairs of a small matrix"
            steps={[
                { text: "Form $A-\\lambda I$ with $\\lambda$ as an unknown on the diagonal.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "Compute $p(\\lambda)=\\det(A-\\lambda I)$ (the characteristic polynomial).", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "Solve $p(\\lambda)=0$ for eigenvalues (factor or use the quadratic formula in the $2\\times 2$ case).", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "For each eigenvalue $\\lambda$, row-reduce $A-\\lambda I$ and solve $(A-\\lambda I)v=0$.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "Write the general nullspace vector; pick one or more independent concrete eigenvectors.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Verify by multiplying: check $Av\\stackrel{?}{=}\\lambda v$ numerically.", why: "Confirm with a second method or by substituting back." },
                { text: "Cross-check: $\\sum\\lambda_i=\\mathrm{tr}(A)$ and $\\prod\\lambda_i=\\det A$.", why: "Compute det with a formula or elimination while tracking signs and scales." }
              ]}
          />
        </section>

        <section className="section" id="la-e-ex-p1">
          <div className="sec-badge">Large examples</div>
          <h2 className="sec-title">Eight detailed worked examples</h2>

          <WorkedExample
            number={1}
            title="Verify an eigenpair"
            setup={"$A=\\begin{pmatrix}3&0\\\\0&1\\end{pmatrix}$, $v=(1,0)$. Claim $\\lambda=3$."}
            steps={[
                { text: "Compute $Av=(3,0)$.", why: "Carry out the computation justified by the setup." },
                { text: "Compute $3v=(3,0)$.", why: "Carry out the computation justified by the setup." },
                { text: "They match, so $v$ is an eigenvector for $\\lambda=3$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Similarly $e_2=(0,1)$ satisfies $Ae_2=(0,1)=1\\cdot e_2$." },
                { text: "Diagonal matrices always have the standard basis as eigenvectors, with diagonal entries as eigenvalues.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Eigenspace for $\\lambda=3$ is $\\mathrm{Span}\\{e_1\\}$; for $\\lambda=1$ it is $\\mathrm{Span}\\{e_2\\}$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
            result={"$v=(1,0)$ is an eigenvector with $\\lambda=3$; also $e_2$ has $\\lambda=1$."}
            check={"$A$ times any $(c,0)$ equals $3(c,0)$."}
          />
          <WorkedExample
            number={2}
            title="Find eigenvalues of a 2×2"
            setup={"$A=\\begin{pmatrix}4&1\\\\2&3\\end{pmatrix}$."}
            steps={[
                { text: "Form $A-\\lambda I=\\begin{pmatrix}4-\\lambda&1\\\\2&3-\\lambda\\end{pmatrix}$.", why: "Eigenvalues are roots of det(A−λI)=0." },
                { text: "Determinant: $(4-\\lambda)(3-\\lambda)-2=\\lambda^2-7\\lambda+12-2=\\lambda^2-7\\lambda+10$.", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "Factor: $(\\lambda-5)(\\lambda-2)=0$." },
                { text: "Eigenvalues $\\lambda=5$ and $\\lambda=2$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Check trace: $4+3=7=5+2$.", why: "Confirm with a second method or by substituting back." },
                { text: "Check det: $12-2=10=5\\cdot 2$.", why: "Compute det with a formula or elimination while tracking signs and scales." }
              ]}
            result={"$\\lambda\\in\\{2,5\\}$."}
            check={"Trace and determinant identities both hold."}
          />
          <WorkedExample
            number={3}
            title="Find an eigenvector for λ=2"
            setup={"Same $A$ as Example 2. Find $v$ for $\\lambda=2$."}
            steps={[
                { text: "$A-2I=\\begin{pmatrix}2&1\\\\2&1\\end{pmatrix}$." },
                { text: "Rows are dependent; the only independent equation is $2x+y=0$.", why: "Use independence and spanning (via rank/pivots) to decide bases and membership." },
                { text: "So $y=-2x$. Free variable $x=t$.", why: "Row-reduce and read pivots, free variables, and consistency from the echelon form." },
                { text: "Take $t=1$: eigenvector $v=(1,-2)$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Verify: $Av=(4-2,\\,2-6)=(2,-4)=2v$.", why: "Confirm with a second method or by substituting back." },
                { text: "Any nonzero multiple of $v$ is also an eigenvector for $\\lambda=2$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
            result={"$v=(1,-2)$ (or any nonzero multiple) for $\\lambda=2$."}
            check={"$A(1,-2)^T=(2,-4)^T=2(1,-2)^T$."}
          />
          <WorkedExample
            number={4}
            title="Eigenvector for λ=5 and basis check"
            setup={"Continue with $A$ from Example 2; find a vector for $\\lambda=5$."}
            steps={[
                { text: "$A-5I=\\begin{pmatrix}-1&1\\\\2&-2\\end{pmatrix}$." },
                { text: "Equation $-x+y=0\\Rightarrow y=x$." },
                { text: "Take $w=(1,1)$." },
                { text: "Verify: $Aw=(4+1,\\,2+3)=(5,5)=5w$.", why: "Confirm with a second method or by substituting back." },
                { text: "Together $v=(1,-2)$ and $w=(1,1)$ are independent (matrix with those columns has $\\det=3\\neq 0$).", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "So they form an eigenbasis of $\\mathbb{R}^2$ — $A$ will be diagonalizable.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." }
              ]}
            result={"$w=(1,1)$ for $\\lambda=5$; $\\{v,w\\}$ is an eigenbasis."}
            check={"$\\det\\begin{pmatrix}1&1\\\\-2&1\\end{pmatrix}=3\\neq 0$."}
          />
          <WorkedExample
            number={5}
            title="Eigenvalues of a projection"
            setup={"$P=\\begin{pmatrix}1&0\\\\0&0\\end{pmatrix}$ (projection onto the $x$-axis). Find eigenpairs."}
            steps={[
                { text: "$P-\\lambda I=\\begin{pmatrix}1-\\lambda&0\\\\0&-\\lambda\\end{pmatrix}$; $\\det=-\\lambda(1-\\lambda)$.", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "Eigenvalues $\\lambda=0$ and $\\lambda=1$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "For $\\lambda=1$: $(P-I)=\\begin{pmatrix}0&0\\\\0&-1\\end{pmatrix}\\Rightarrow$ second coordinate $0$. Eigenvectors $(x,0)$ with $x\\neq 0$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "For $\\lambda=0$: $Pv=0\\Rightarrow$ first coordinate $0$. Eigenvectors $(0,y)$ with $y\\neq 0$.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Interpretation: vectors on the axis stay put ($\\lambda=1$); vertical vectors collapse to $0$ ($\\lambda=0$).", why: "Interpret Ax as a combination of the columns of A." },
                { text: "Trace $1=1+0$ and $\\det 0=1\\cdot 0$ check out.", why: "Compute det with a formula or elimination while tracking signs and scales." }
              ]}
            result={"$\\lambda=1$ along $e_1$; $\\lambda=0$ along $e_2$."}
            check={"$P(3,0)=(3,0)$ and $P(0,4)=(0,0)$."}
          />
          <WorkedExample
            number={6}
            title="Complex eigenvalues warning"
            setup={"$R=\\begin{pmatrix}0&-1\\\\1&0\\end{pmatrix}$. Show there is no real eigenpair."}
            steps={[
                { text: "$\\det(R-\\lambda I)=\\lambda^2+1$.", why: "Compute det with a formula or elimination while tracking signs and scales." },
                { text: "Roots $\\lambda=\\pm i$ are not real." },
                { text: "Suppose $Rv=\\lambda v$ for real $v\\neq 0$ and real $\\lambda$. Then $R$ would scale $v$, but $R$ rotates by $90^\\circ$." },
                { text: "A $90^\\circ$ rotation never preserves a real line through the origin." },
                { text: "Therefore no real eigenvectors exist, even though $R$ is invertible.", why: "Use (A−λI)v=0 for each eigenvalue and read the nullspace." },
                { text: "Over $\\mathbb{C}$ the story continues; over $\\mathbb{R}$ you need a different normal form (rotation-scaling blocks).", why: "Use dots and norms for length, angle, and orthogonal projection." }
              ]}
            result={"No real eigenvalues or eigenvectors for $R$."}
            check={"$\\lambda^2+1=0$ has discriminant $-4<0$."}
          />

          <WorkedExample
            number={7}
            title="Repeated eigenvalue with enough eigenvectors"
            setup={"$A=\\operatorname{diag}(2,2,5)$. Compare algebraic and geometric multiplicities and decide whether $A$ is diagonalizable."}
            steps={[
                { text: "$\\det(A-\\lambda I)=(2-\\lambda)^2(5-\\lambda)$, so $\\lambda=2$ has algebraic multiplicity $2$ and $\\lambda=5$ has algebraic multiplicity $1$." },
                { text: "$A-2I=\\operatorname{diag}(0,0,3)$, so $E_2=\\operatorname{Nul}(A-2I)=\\operatorname{Span}\\{e_1,e_2\\}$." },
                { text: "Therefore the geometric multiplicity of $\\lambda=2$ is $2$, equal to its algebraic multiplicity." },
                { text: "$E_5=\\operatorname{Span}\\{e_3\\}$, so the geometric multiplicity of $5$ is $1$." },
                { text: "The total number of independent eigenvectors is $2+1=3$, which equals the matrix size." },
                { text: "Hence $A$ is diagonalizable; in fact it is already diagonal." }
              ]}
            result={"For $\\lambda=2$, $AM=GM=2$; for $\\lambda=5$, $AM=GM=1$. The matrix is diagonalizable."}
            check={"A repeated eigenvalue does not cause a problem when its eigenspace has the required dimension."}
          />
          <WorkedExample
            number={8}
            title="Same eigenvalues, but a defective matrix"
            setup={"$B=\\begin{pmatrix}2&1&0\\\\0&2&0\\\\0&0&5\\end{pmatrix}$. Compare its multiplicities with Example 7."}
            steps={[
                { text: "$B$ is triangular, so $\\det(B-\\lambda I)=(2-\\lambda)^2(5-\\lambda)$. The algebraic multiplicities are unchanged." },
                { text: "$B-2I=\\begin{pmatrix}0&1&0\\\\0&0&0\\\\0&0&3\\end{pmatrix}$, so $y=0$ and $z=0$ while $x$ is free." },
                { text: "Thus $E_2=\\operatorname{Span}\\{(1,0,0)\\}$ and $GM(2)=1<2=AM(2)$." },
                { text: "For $\\lambda=5$, the eigenspace is $E_5=\\operatorname{Span}\\{(0,0,1)\\}$, so $GM(5)=1$." },
                { text: "Only $1+1=2$ independent eigenvectors are available for a $3\\times3$ matrix." },
                { text: "Therefore $B$ is defective and cannot be diagonalized, even though it has the same eigenvalues and algebraic multiplicities as Example 7." }
              ]}
            result={"$GM(2)=1<AM(2)=2$, so $B$ is not diagonalizable."}
            check={"The comparison isolates the real issue: diagonalizability depends on eigenspace dimensions, not merely on repeated roots."}
          />
        </section>

        <LaMcqSection
          id="quiz-la-e-intro"
          badge="Quiz 4.1"
          title="Eigen basics"
          scoreId="score-la-e-intro"
          section="la-e-intro"
          questions={LA_E_INTRO_QUIZ}
        />

        <Divider />

        <section className="section" id="la-e-char">
          <div className="sec-badge">Section 4.2</div>
          <h2 className="sec-title">Characteristic polynomial — deep theory</h2>
          <TheoryBox title="p(λ) = det(A − λI)">
            <p>
              {"Using the convention $p_A(\\lambda)=\\det(A-\\lambda I)$, the characteristic polynomial has degree $n$ and leading coefficient $(-1)^n$. The alternative convention $\\chi_A(\\lambda)=\\det(\\lambda I-A)=(-1)^n p_A(\\lambda)$ is monic. Both have exactly the same roots, so either convention gives the same eigenvalues. For each root $\\lambda$, the eigenspace is $\\mathrm{Nul}(A-\\lambda I)$."}
            </p>
            <p>
              {"Algebraic multiplicity is the root’s multiplicity in $p$. Geometric multiplicity is $\\dim\\mathrm{Nul}(A-\\lambda I)$, always at least $1$ for an eigenvalue and at most the algebraic multiplicity. The gap between them is exactly what obstructs diagonalization."}
            </p>
          </TheoryBox>
          <TheoremBox title="Multiplicities and Cayley–Hamilton">
            <p>
              {"Cayley–Hamilton says $p(A)=0$ as a matrix identity — every matrix satisfies its own characteristic equation. Practically, for $2\\times 2$ work you mainly need: factor $p$, read algebraic multiplicities, then compute geometric multiplicities by nullspaces. Trace and determinant recover the elementary symmetric data of the eigenvalues."}
            </p>
          </TheoremBox>
        </section>
        <section className="section" id="la-e-multiplicity">
          <div className="sec-badge">Multiplicity</div>
          <h2 className="sec-title">Algebraic vs. geometric multiplicity</h2>
          <TheoryBox title="Two different multiplicities">
            <p>
              {"For an eigenvalue $\\lambda$, the algebraic multiplicity $AM(\\lambda)$ is its multiplicity as a root of the characteristic polynomial. The geometric multiplicity $GM(\\lambda)$ is the dimension of its eigenspace: $GM(\\lambda)=\\dim\\operatorname{Nul}(A-\\lambda I)$."}
            </p>
            <p>
              {"Every eigenvalue satisfies $1\\le GM(\\lambda)\\le AM(\\lambda)$. Algebraic multiplicity counts how many times the root occurs; geometric multiplicity counts how many independent eigenvector directions are actually available for that eigenvalue."}
            </p>
          </TheoryBox>
          <TheoremBox title="Multiplicity test for diagonalizability">
            <p>
              {"Suppose the characteristic polynomial of an $n\\times n$ matrix splits into linear factors over the field being used. Then $A$ is diagonalizable exactly when the geometric multiplicity equals the algebraic multiplicity for every eigenvalue. Equivalently, the sum of all eigenspace dimensions is $n$. Distinct eigenvalues automatically give independent eigenvectors, but repeated eigenvalues require this dimension check."}
            </p>
          </TheoremBox>
          <PracticalTheory title="Fast repeated-eigenvalue check">
            <p>
              {"After factoring the characteristic polynomial, record each eigenvalue and its algebraic multiplicity. For every repeated eigenvalue, row-reduce $A-\\lambda I$ and count free variables; that nullity is the geometric multiplicity. If any $GM<AM$, the matrix is defective. If all multiplicities match and the polynomial splits, an eigenbasis exists."}
            </p>
          </PracticalTheory>
          <RealLifeUse>{"Repeated modes occur in symmetric engineering models when a system has geometric symmetry. Symmetry does not automatically create a defect: for real symmetric matrices the Spectral Theorem guarantees enough orthogonal eigenvectors even when eigenvalues repeat."}</RealLifeUse>
        </section>


        <LaMcqSection
          id="quiz-la-e-char"
          badge="Quiz 4.2"
          title="Characteristic polynomial & multiplicity"
          scoreId="score-la-e-char"
          section="la-e-char"
          questions={LA_E_CHAR_QUIZ}
        />

        <Divider />
        <LaCertificateBoost topic="eigen" part={1} />

        <section className="section" id="summary1">
          <div className="sec-badge">Reference</div>
          <h2 className="sec-title">Continue</h2>
          <p>
            {"Eigenpairs capture invariant directions; the characteristic polynomial finds the stretch factors. Part 2 assembles them into diagonalization and applications."}
          </p>
          <p>
            Use the gold button: <strong>Next: Part 2 — Diagonalization</strong>.
          </p>
        </section>
      </main>
    </StudyGuideShell>
  );
}

export default EigenGuide;
